import React, { useState, useEffect, useMemo } from 'react';
import { Tree, ConfigProvider } from 'antd';
import type { DataNode } from 'antd/es/tree';
import {
  Box,
  FormControl,
  FormHelperText,
  Paper,
  Popover,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { Clear, ArrowDropDown } from '@mui/icons-material';
import './VirtualizedTreeSelect.scss'; // Custom styles to match MUI design

export interface TreeSelectItem {
  id: string;
  name: string;
  children?: TreeSelectItem[];
  parent_id?: string;
}

interface VirtualizedTreeSelectProps {
  items: TreeSelectItem[];
  value: string | string[];
  onChange: (value: any) => void;
  onBlur?: () => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  placeholder?: string;
  multiple?: boolean;
  searchable?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  isLoading?: boolean;
  menuWidth?: number;
  minMenuWidth?: number;
  maxMenuWidth?: number;
  // Virtualization props (native to Ant Design)
  height?: number; // Tree height (default: 300)
  itemHeight?: number; // Item height for virtual scrolling (default: 28)
}

const TreeSelect: React.FC<VirtualizedTreeSelectProps> = ({
  items,
  value,
  onChange,
  onBlur,
  label,
  error = false,
  helperText = '',
  disabled = false,
  placeholder = 'Select an item',
  multiple = false,
  searchable = false,
  searchValue = '',
  onSearchChange,
  isLoading = false,
  menuWidth = 220,
  minMenuWidth,
  maxMenuWidth,
  height = 300,
  itemHeight = 28,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedLabel, setSelectedLabel] = useState<string>('');

  const theme = useTheme();

  // Build lookup map for fast access
  const itemLookup = useMemo(() => {
    const map: Record<string, TreeSelectItem> = {};
    const stack = [...items];
    while (stack.length) {
      const node = stack.pop()!;
      map[node.id] = node;
      if (node.children?.length) stack.push(...node.children);
    }
    return map;
  }, [items]);

  // Helper function to get all descendant IDs
  const getAllDescendantIds = (nodeId: string): string[] => {
    const node = itemLookup[nodeId];
    if (!node || !node.children?.length) return [];
    
    const descendants: string[] = [];
    const stack = [...node.children];
    
    while (stack.length) {
      const current = stack.pop()!;
      descendants.push(current.id);
      if (current.children?.length) {
        stack.push(...current.children);
      }
    }
    
    return descendants;
  };

  // Transform to Ant Design DataNode format
  const transformToDataNode = (nodes: TreeSelectItem[]): DataNode[] => {
    return nodes.map((node) => ({
      key: node.id,
      title: node.name,
      children: node.children?.length ? transformToDataNode(node.children) : undefined,
    }));
  };

  const treeData = useMemo(() => transformToDataNode(items), [items]);

  // Update selected label
  useEffect(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setSelectedLabel('');
      return;
    }

    if (multiple && Array.isArray(value)) {
      const labels = value
        .map((id) => itemLookup[id]?.name)
        .filter(Boolean) as string[];
      setSelectedLabel(
        labels.length > 1 ? `${labels.length} items selected` : labels.join(', ')
      );
    } else {
      setSelectedLabel(itemLookup[value as string]?.name || '');
    }
  }, [value, itemLookup, multiple]);

  // Filter tree based on search
  const getFilteredTreeData = (data: DataNode[], search: string): DataNode[] => {
    if (!search) return data;

    const lowerSearch = search.toLowerCase();
    const filterNode = (node: DataNode): DataNode | null => {
      const titleMatch = String(node.title).toLowerCase().includes(lowerSearch);
      const filteredChildren = node.children
        ?.map((child) => filterNode(child))
        .filter(Boolean) as DataNode[];

      if (titleMatch || filteredChildren?.length > 0) {
        return {
          ...node,
          children: filteredChildren?.length > 0 ? filteredChildren : undefined,
        };
      }
      return null;
    };

    return data.map((node) => filterNode(node)).filter(Boolean) as DataNode[];
  };

  const filteredData = useMemo(
    () => getFilteredTreeData(treeData, searchText),
    [treeData, searchText]
  );

  // Auto-expand all when searching
  useEffect(() => {
    if (searchText) {
      const allKeys: React.Key[] = [];
      const collectKeys = (nodes: DataNode[]) => {
        nodes.forEach((node) => {
          allKeys.push(node.key);
          if (node.children) collectKeys(node.children);
        });
      };
      collectKeys(filteredData);
      setExpandedKeys(allKeys);
    } else {
      setExpandedKeys([]);
    }
  }, [searchText, filteredData]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled) setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchText('');
    onBlur?.();
  };

  const handleSelect = (selectedKeys: React.Key[], info: any) => {
    if (multiple) {
      onChange(selectedKeys.map(String));
    } else {
      const selectedKey = selectedKeys[0];
      onChange(selectedKey ? String(selectedKey) : '');
      
      // Close if leaf node selected
      const selectedNode = itemLookup[String(selectedKey)];
      if (!selectedNode?.children?.length) {
        handleClose();
      }
    }
  };

  const handleCheck = (checkedKeys: any, info: any) => {
    // Get the node that was just checked/unchecked
    const { node, checked } = info;
    const nodeId = String(node.key);
    
    let newCheckedKeys: string[];
    
    if (checkedKeys?.checked) {
      newCheckedKeys = checkedKeys.checked.map(String);
    } else {
      newCheckedKeys = checkedKeys.map(String);
    }
    
    if (checked) {
      // When checking a node, add all its descendants
      const descendants = getAllDescendantIds(nodeId);
      const allToAdd = [nodeId, ...descendants];
      
      // Add all descendants to the checked keys
      const updatedKeys = [...new Set([...newCheckedKeys, ...allToAdd])];
      onChange(updatedKeys);
    } else {
      // When unchecking, only remove that specific node (not its children or parents)
      const updatedKeys = newCheckedKeys.filter(key => key !== nodeId);
      onChange(updatedKeys);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(multiple ? [] : '');
    setExpandedKeys([]);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchText(val);
    onSearchChange?.(val);
  };

  const hasValue =
    (Array.isArray(value) && value.length > 0) ||
    (!!value && !Array.isArray(value));

  const open = Boolean(anchorEl);

  // Normalize value for Ant Design
  const normalizedSelectedKeys = useMemo(() => {
    if (Array.isArray(value)) return value;
    return value ? [value] : [];
  }, [value]);

  const showError =
    error && (!value || (Array.isArray(value) && value.length === 0));

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: theme.palette.primary.main,
          borderRadius: 6,
          fontFamily: theme.typography.fontFamily,
        },
      }}
    >
    <FormControl
      fullWidth
      error={error && (!value || (Array.isArray(value) && value.length === 0))}
    >
      <Box
        onClick={handleClick}
        sx={{
          borderRadius: '4px',
          padding: '6px',
          height: '40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: disabled ? 'rgba(0,0,0,0.12)' : '#EFF3F6',
          cursor: disabled ? 'default' : 'pointer',
          border: showError ? `1px solid ${theme.palette.error.main}` : 'none',
        }}
      >
        <Typography
          color={selectedLabel ? 'textPrimary' : 'textDisabled'}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '14px',
          }}
        >
          {selectedLabel || placeholder}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {hasValue && !disabled && (
            <IconButton size="small" onClick={handleClear}>
              <Clear fontSize="small" />
            </IconButton>
          )}
          <ArrowDropDown
            sx={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        </Box>
      </Box>

      {helperText && (!value || (Array.isArray(value) && value.length === 0)) && (
        <FormHelperText error>{helperText}</FormHelperText>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          // Don't close when loading (API call in progress)
          if (!isLoading) {
            handleClose();
          }
        }}
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: menuWidth,
            minWidth: Math.max(anchorEl?.clientWidth || 0, minMenuWidth ?? 260),
            maxWidth: maxMenuWidth ?? 360,
            mt: 0.5,
          },
        }}
      >
        <Paper
          sx={{
            p: 1.5,

            '& .ant-tree-checkbox': {
              borderColor: '#7f8995',
              borderWidth: '2px',
              borderRadius: '3px',
            },
          }}
        >
          {searchable && (
            <TextField
              size="small"
              fullWidth
              placeholder="Search..."
              value={searchValue || searchText}
              onChange={handleSearchChange}
              sx={{ mb: 1.5 }}
              InputProps={{
                endAdornment: (searchValue || searchText) && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchText('');
                        onSearchChange?.('');
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          {filteredData.length === 0 && searchText ? (
            <Typography
              sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}
            >
              No results found
            </Typography>
          ) : (
            <Box sx={{ overflow: 'hidden' }}>
              <Tree
                treeData={filteredData}
                checkable={multiple}
                checkedKeys={multiple ? normalizedSelectedKeys : undefined}
                selectedKeys={!multiple ? normalizedSelectedKeys : undefined}
                onCheck={multiple ? handleCheck : undefined}
                onSelect={!multiple ? handleSelect : undefined}
                expandedKeys={expandedKeys}
                onExpand={setExpandedKeys}
                checkStrictly={true}
                // ✅ VIRTUALIZATION - Key prop for performance
                virtual
                height={height}
                itemHeight={itemHeight}
                // Style overrides to match MUI
                style={{
                  backgroundColor: 'transparent',
                  fontSize: '14px',
                }}
                // Disable animation for better performance
                motion={null}
              />
            </Box>
          )}

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}
        </Paper>
      </Popover>
    </FormControl>
    </ConfigProvider>
  );
};

export default TreeSelect;